import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download, Search, RotateCcw, Loader2, CalendarSearch, Mail, FileText, BarChart3 } from 'lucide-react';

const ALL = 'all';
const gbp = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;

const DATE_FIELDS = [
  { value: 'contract_signed_date', label: 'Contract signed' },
  { value: 'deposit_paid_date', label: 'Deposit paid' },
  { value: 'dno_application_submitted_date', label: 'DNO raised' },
  { value: 'created_at', label: 'Customer created' },
];

const iso = (d) => d.toISOString().split('T')[0];

// Quick date-range presets
const PRESETS = [
  {
    key: 'this_month', label: 'This month',
    range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), n]; },
  },
  {
    key: 'last_month', label: 'Last month',
    range: () => { const n = new Date(); return [new Date(n.getFullYear(), n.getMonth() - 1, 1), new Date(n.getFullYear(), n.getMonth(), 0)]; },
  },
  {
    key: 'this_quarter', label: 'This quarter',
    range: () => { const n = new Date(); const q = Math.floor(n.getMonth() / 3) * 3; return [new Date(n.getFullYear(), q, 1), n]; },
  },
  {
    key: 'this_year', label: 'This year',
    range: () => { const n = new Date(); return [new Date(n.getFullYear(), 0, 1), n]; },
  },
  {
    key: 'last_30', label: 'Last 30 days',
    range: () => { const n = new Date(); const s = new Date(); s.setDate(n.getDate() - 29); return [s, n]; },
  },
];

export const DateSearchReport = () => {
  const [field, setField] = useState('contract_signed_date');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [status, setStatus] = useState(ALL);
  const [installType, setInstallType] = useState(ALL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [trendMetric, setTrendMetric] = useState('count'); // 'count' | 'value'
  // Email report dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // Scheduled email config
  const [sched, setSched] = useState({ enabled: false, recipients: '', loaded: false });
  const [savingSched, setSavingSched] = useState(false);

  const fieldLabel = DATE_FIELDS.find((d) => d.value === field)?.label || 'Date';

  useEffect(() => {
    api.get('/reports/scheduled-report')
      .then((r) => setSched({
        enabled: !!r.data.enabled,
        recipients: (r.data.recipients || []).join(', '),
        field: r.data.field,
        loaded: true,
      }))
      .catch(() => setSched((s) => ({ ...s, loaded: true })));
  }, []);

  const buildParams = () => {
    const p = { field };
    if (start) p.start = start;
    if (end) p.end = end;
    if (start && !end) p.end = start;
    if (status !== ALL) p.status = status;
    if (installType !== ALL) p.installation_type = installType;
    return p;
  };

  const run = async (override) => {
    setLoading(true);
    try {
      const params = override || buildParams();
      const { data } = await api.get('/reports/date-search', { params });
      setData(data);
    } catch {
      toast.error('Failed to run report');
    } finally {
      setLoading(false);
    }
  };

  const applyPreset = (preset) => {
    const [s, e] = preset.range();
    setStart(iso(s));
    setEnd(iso(e));
    const p = { field, start: iso(s), end: iso(e) };
    if (status !== ALL) p.status = status;
    if (installType !== ALL) p.installation_type = installType;
    run(p);
  };

  const reset = () => { setStart(''); setEnd(''); setStatus(ALL); setInstallType(ALL); setData(null); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await api.get('/reports/date-search/export/csv', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${field}_report_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get('/reports/date-search/export/pdf', { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${field}_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported');
    } catch {
      toast.error('PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  const sendEmail = async () => {
    const recipients = emailTo.split(',').map((e) => e.trim()).filter(Boolean);
    if (!recipients.length) { toast.error('Add at least one recipient'); return; }
    setSendingEmail(true);
    try {
      const { data: res } = await api.post('/reports/date-search/email', { ...buildParams(), recipients });
      toast.success(`Report emailed to ${res.emailed}/${res.recipients} recipient(s)`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to email report');
    } finally {
      setSendingEmail(false);
    }
  };

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      const recipients = sched.recipients.split(',').map((e) => e.trim()).filter(Boolean);
      await api.post('/reports/scheduled-report', {
        enabled: sched.enabled,
        field,
        recipients,
        status: status !== ALL ? status : null,
        installation_type: installType !== ALL ? installType : null,
      });
      toast.success(sched.enabled ? 'Monthly email scheduled' : 'Schedule saved (off)');
    } catch {
      toast.error('Failed to save schedule');
    } finally {
      setSavingSched(false);
    }
  };

  const statuses = data?.available_statuses || [];
  const instTypes = data?.available_installation_types || [];

  return (
    <div className="space-y-4" data-testid="date-search-report">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Date-based report</CardTitle>
          <CardDescription>
            Pick what to search on, use a preset or a custom single date / range, and optionally narrow by status or type.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick presets */}
          <div className="flex flex-wrap items-center gap-2" data-testid="date-presets">
            <span className="text-xs font-medium text-slate-400 mr-1">Quick range:</span>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p)}
                className="text-xs font-medium px-3 py-1.5 rounded-full border bg-white text-slate-600 border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                data-testid={`date-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Search by</Label>
              <Select value={field} onValueChange={(v) => { setField(v); setData(null); }}>
                <SelectTrigger className="h-9" data-testid="date-field-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DATE_FIELDS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="date-search-start" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">To (optional)</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="date-search-end" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => run()} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white flex-1" data-testid="date-search-run-btn">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Run
              </Button>
              <Button variant="outline" onClick={reset} data-testid="date-search-reset-btn"><RotateCcw className="w-4 h-4" /></Button>
            </div>
          </div>

          {/* Extra filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9" data-testid="date-status-select"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any status</SelectItem>
                  {statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Installation type</Label>
              <Select value={installType} onValueChange={setInstallType}>
                <SelectTrigger className="h-9" data-testid="date-insttype-select"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Any type</SelectItem>
                  {instTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end justify-end gap-2">
              <Button variant="outline" onClick={exportCsv} disabled={exporting || !data?.total} data-testid="date-search-export-btn">
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} CSV
              </Button>
              <Button variant="outline" onClick={exportPdf} disabled={exportingPdf || !data?.total} data-testid="date-search-export-pdf-btn">
                {exportingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />} PDF
              </Button>
              <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={!data?.total} data-testid="date-search-email-btn">
                <Mail className="w-4 h-4 mr-2" /> Email
              </Button>
            </div>
          </div>
          {(status !== ALL || installType !== ALL) && (
            <p className="text-xs text-slate-400">Tip: run again after changing Status/Type to apply the new filters. Status/type options populate after the first Run.</p>
          )}
        </CardContent>
      </Card>

      {data && (
        <div className="grid grid-cols-2 gap-3" data-testid="date-search-summary">
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Matches ({data.field_label})</p>
            <p className="text-2xl font-bold text-slate-900">{data.total}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5">
            <p className="text-xs text-slate-500">Total value</p>
            <p className="text-2xl font-bold text-slate-900">{gbp(data.total_value)}</p>
          </CardContent></Card>
        </div>
      )}

      {data?.trend?.length > 0 && (
        <Card data-testid="date-trend-card">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-600" /> Trend by {data.bucket_type === 'month' ? 'month' : 'week'}
              </CardTitle>
              <CardDescription>{trendMetric === 'count' ? 'Number of records' : 'Total value (£)'} per {data.bucket_type === 'month' ? 'month' : 'week'}</CardDescription>
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
              <button
                type="button"
                onClick={() => setTrendMetric('count')}
                className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${trendMetric === 'count' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500'}`}
                data-testid="trend-metric-count"
              >Count</button>
              <button
                type="button"
                onClick={() => setTrendMetric('value')}
                className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${trendMetric === 'value' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}
                data-testid="trend-metric-value"
              >Value (£)</button>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.trend} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickFormatter={(v) => trendMetric === 'value' ? `£${(v / 1000).toFixed(0)}k` : v}
                  allowDecimals={false}
                  width={48}
                />
                <Tooltip
                  formatter={(v) => trendMetric === 'value' ? [gbp(v), 'Value'] : [v, 'Records']}
                  labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 }}
                />
                <Bar
                  dataKey={trendMetric}
                  fill={trendMetric === 'count' ? '#2563eb' : '#0f766e'}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          ) : (data?.data?.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Postcode</TableHead>
                    <TableHead>{fieldLabel}</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((r) => (
                    <TableRow key={r.id} data-testid={`date-row-${r.id}`}>
                      <TableCell>
                        <p className="font-medium text-slate-800">{r.name}</p>
                        <p className="text-xs text-slate-400">{r.email}</p>
                      </TableCell>
                      <TableCell className="text-sm">{r.postcode || '—'}</TableCell>
                      <TableCell className="text-sm font-medium">{r.date_value || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <Badge variant="secondary" className="text-xs capitalize">{(r.status || '').replace(/_/g, ' ') || '—'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.installation_type || '—'}</TableCell>
                      <TableCell className="text-sm">{gbp(r.system_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-40 text-slate-400" data-testid="date-search-empty">
              <CalendarSearch className="w-8 h-8 mb-2" />
              <p className="text-sm">{data ? 'No customers match this date range.' : 'Choose a field and date, then Run.'}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Scheduled monthly email */}
      <Card data-testid="schedule-report-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600" /> Email this report monthly</CardTitle>
          <CardDescription>
            On the 1st of each month, email <strong>last month's</strong> "{fieldLabel}" results (with the current Status/Type filters) as a PDF and CSV.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={sched.enabled} onCheckedChange={(v) => setSched((s) => ({ ...s, enabled: v }))} data-testid="schedule-enabled-switch" />
            <span className="text-sm text-slate-600">{sched.enabled ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Recipients (comma-separated emails)</Label>
            <Input
              placeholder="nick@helioses.co.uk, office@helioses.co.uk"
              value={sched.recipients}
              onChange={(e) => setSched((s) => ({ ...s, recipients: e.target.value }))}
              className="h-9"
              data-testid="schedule-recipients-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={saveSchedule} disabled={savingSched} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="schedule-save-btn">
              {savingSched ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Save schedule
            </Button>
            <span className="text-xs text-slate-400">Uses the "Search by" field selected above.</span>
          </div>
        </CardContent>
      </Card>

      {/* Email report now dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-report-dialog">
          <DialogHeader>
            <DialogTitle>Email this report</DialogTitle>
            <DialogDescription>
              Sends the current "{fieldLabel}" results (with the selected filters) as a PDF and CSV to the recipients below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Recipients (comma-separated emails)</Label>
            <Input
              placeholder="nick@helioses.co.uk, office@helioses.co.uk"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="h-9"
              data-testid="email-report-recipients"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} data-testid="email-report-cancel">Cancel</Button>
            <Button onClick={sendEmail} disabled={sendingEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="email-report-send">
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DateSearchReport;
